import { useEffect, useMemo, useRef, useState } from 'react';
import { useInboxStore } from '../../stores/inbox-store';
import { supabase } from '../../lib/supabase';
import { isBezorgbaarEmail } from '../../lib/email/bezorgbaar';
import { isEigenEmail, isEigenTelefoon } from '../../lib/email/eigen-adres';
import { kiesActueleKlantbronBasis } from '../../lib/inbox/effectieve-klantbron';

interface Props {
  onClose: () => void;
  /** Toast-tekst bij volledige succes (reply verstuurd + doorverwijzing geregistreerd). */
  onSuccess?: (message: string) => void;
}

interface DoorverwijsPartner {
  /** `partnerId|segment` — de keuze-eenheid; een partner kan meerdere segmenten doen. */
  key: string;
  partnerId: string;
  naam: string;
  segment: string;
  primarySku: string;
  ccEmail: string;
  replyTemplate: string;
  specialisme: string;
  commissieType: string;
  commissieValue: number;
  commissieLabel: string;
  /**
   * Rol in het postcodegebied van de klant. Leeg zolang er geen postcode is.
   * 'uitvoerder' = deze partner staat op de gebiedstoewijzing voor dit segment.
   */
  gebiedsrol?: 'uitvoerder' | 'andere-partner' | 'niet-toegewezen';
}

/** Het postcodegebied van de klant plus wie er per segment de uitvoerder is. */
interface Gebied {
  postcode: number;
  gebiedNaam: string | null;
  meerdereGebieden: boolean;
  uitvoerders: Record<string, { partnerId: string | null; partnerNaam: string | null }>;
}

/**
 * Partner die wel op het platform actief is maar nog niet doorverwezen kan
 * worden. Staat als uitgegrijsde regel in de dropdown zodat zichtbaar is dat hij
 * bestaat én wat eraan ontbreekt.
 */
interface OnvolledigePartner {
  partnerId: string;
  naam: string;
  segment: string;
  redenen: string[];
}

/**
 * Goedgekeurde AI-partneraanvraag die al op dit gesprek staat (uit de preflight
 * van /api/inbox/doorverwijzen). Dan verstuurt deze dialoog díe aanvraag en
 * maakt hij geen tweede lead (Huygen 03-11: L-312994 én L-313085).
 */
interface BestaandeAanvraag {
  status: string;
  leadNummer: string;
  partnerId: string;
  partnerNaam: string;
  segment: string;
  ccEmail: string;
  klantEmail: string;
}

/** Antwoord van /api/inbox/doorverwijs-prefill — klant uit de mailbody gevist. */
interface AiPrefill {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  postcode: string;
  city: string;
  context: string;
}

/** Splits een volledige naam in voornaam + achternaam (SF eist LastName). */
function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: '', lastName: parts[0] ?? '' };
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}

/** Vul `{{voornaam}}` in; bij lege voornaam wordt "Hoi {{voornaam}}," → "Hoi,". */
function fillTemplate(template: string, voornaam: string): string {
  if (voornaam.trim()) return template.replace(/\{\{voornaam\}\}/g, voornaam.trim());
  return template.replace(/\s*\{\{voornaam\}\}/g, '');
}

export function DoorverwijsDialog({ onClose, onSuccess }: Props) {
  const activeThread = useInboxStore((s) => s.activeThread);
  const conversation = activeThread?.conversation;
  const customerMatch = activeThread?.customer;

  // ── Klant-prefill uit de thread (eenmalig, bewerkbaar) ──
  const prefill = useMemo(() => {
    const conv = activeThread?.conversation;
    const bronkeuze = kiesActueleKlantbronBasis(activeThread?.messages ?? [], conv ?? {});
    const klantbron = bronkeuze.status === 'gevonden' ? bronkeuze.bron : null;
    // Bij telefoon/terugbel-threads is de afzender de antwoordservice (BizziPhone)
    // of het telefoonnummer — niet de klant. Gebruik dan géén afzender-naam/e-mail;
    // de AI-prefill vist de echte klant uit de belnotitie. Bij e-mailthreads de
    // normale afzender-keten.
    const isPhoneLike = conv?.channel === 'phone' || conv?.subtype === 'callback';
    const fullName =
      customerMatch?.customer?.name ||
      conv?.customer_name ||
      (isPhoneLike ? '' : (klantbron?.afzenderNaam || conv?.effective_sender?.name || conv?.from_name)) ||
      '';
    const { firstName, lastName } = splitName(fullName);
    const emailCandidates = isPhoneLike
      ? [customerMatch?.customer?.email]
      : [klantbron?.afzender, conv?.effective_sender?.email, conv?.from_email, customerMatch?.customer?.email];
    // Eigen mailbox of kantoornummer is nooit de klant (Huygen 03-11).
    const email = emailCandidates.find((e) => e && e.includes('@') && !isEigenEmail(e)) || '';
    const phoneRuw = conv?.phone || customerMatch?.customer?.phone || '';
    const phone = isEigenTelefoon(phoneRuw) ? '' : phoneRuw;
    return { firstName, lastName, email, phone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [partners, setPartners] = useState<DoorverwijsPartner[]>([]);
  const [onvolledig, setOnvolledig] = useState<OnvolledigePartner[]>([]);
  const [gebied, setGebied] = useState<Gebied | null>(null);
  // Zodra de rep zelf een partner kiest, mag de gebiedshint die keuze niet
  // meer overschrijven.
  const partnerHandmatig = useRef(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [aiPrefilled, setAiPrefilled] = useState(false);

  const [firstName, setFirstName] = useState(prefill.firstName);
  const [lastName, setLastName] = useState(prefill.lastName);
  const [email, setEmail] = useState(prefill.email);
  const [phone, setPhone] = useState(prefill.phone);
  const [postcode, setPostcode] = useState('');
  const [geschatBedrag, setGeschatBedrag] = useState('');
  const [cc, setCc] = useState('');
  const [replyText, setReplyText] = useState('');
  const [note, setNote] = useState('');
  const [replyTouched, setReplyTouched] = useState(false);
  const [extraContext, setExtraContext] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  const [phase, setPhase] = useState<'idle' | 'sending' | 'registering'>('idle');
  const [replySent, setReplySent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Tekst en partner zijn aan elkaar gebonden (Huygen 03-11: tekst over
  // CleanProfit verstuurd met De Meubelreiniger in de cc). `replyVoorKey` is
  // de partner waarvoor de tekst is geschreven; wijkt die af van de keuze,
  // dan is versturen uit tot de nieuwe tekst binnen is.
  const [replyVoorKey, setReplyVoorKey] = useState('');
  const [replyBezig, setReplyBezig] = useState(false);
  const [bestaand, setBestaand] = useState<BestaandeAanvraag | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const isBusy = phase !== 'idle';

  const selectedPartner = partners.find((p) => p.key === selectedKey) || null;

  // %-partners (bv. Dakkapelreiniging 10%): toon een optioneel "geschat bedrag"
  // zodat de commissie-schatting klopt. Bij Fixed-partners niet relevant.
  const isPercentagePartner = selectedPartner?.commissieType === 'Percentage';
  const bedragNum = parseFloat(geschatBedrag.replace(',', '.'));
  const commissiePreview =
    isPercentagePartner && Number.isFinite(bedragNum) && bedragNum > 0
      ? bedragNum * (selectedPartner!.commissieValue / 100)
      : null;

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isBusy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isBusy, onClose]);

  // Roep de prefill-endpoint aan: vist klant + context uit de mailbody én laat
  // (met partner) een op de áánvraag afgestemde reply opstellen.
  const fetchPrefill = async (partner: DoorverwijsPartner | null, steer?: string) => {
    if (!conversation) return null;
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return null;
    const res = await fetch('/api/inbox/doorverwijs-prefill', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversation_id: conversation.id,
        partnerNaam: partner?.naam,
        partnerSegment: partner?.segment,
        partnerSpecialisme: partner?.specialisme,
        extraContext: steer?.trim() || undefined,
      }),
    });
    if (!res.ok) return null;
    return res.json().catch(() => null) as Promise<{ prefill?: Partial<AiPrefill>; reply?: string } | null>;
  };

  // Preflight op /api/inbox/doorverwijzen: dezelfde regels als de registratie
  // (eigen adres, partnernaam in de tekst, bestaande aanvraag), zonder write.
  // Gate en uitvoerder delen de regels; de dialoog toont alleen de uitkomst.
  const fetchPreflight = async (payload: Record<string, unknown>) => {
    if (!conversation) return { ok: false as const, error: 'Geen gesprek' };
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return { ok: false as const, error: 'Niet ingelogd' };
    const res = await fetch('/api/inbox/doorverwijzen', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversation.id, prepare_only: true, ...payload }),
    });
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; code?: string; bestaand?: BestaandeAanvraag | null };
    if (!res.ok) return { ok: false as const, error: body.error || 'Controle mislukt', code: body.code, bestaand: body.bestaand ?? null };
    return { ok: true as const, bestaand: body.bestaand ?? null };
  };

  // Genereer de reply opnieuw, gestuurd door de extra context van de medewerker.
  // Raakt alleen de mailtekst — de al gecontroleerde klantvelden blijven staan.
  const regenerateReply = async () => {
    if (regenerating || isBusy || !selectedPartner) return;
    setRegenerating(true);
    setError(null);
    try {
      const data = await fetchPrefill(selectedPartner, extraContext);
      const aiReply = data ? String(data.reply || '').trim() : '';
      if (aiReply) {
        setReplyText(aiReply);
        setReplyTouched(false);
        setReplyVoorKey(selectedPartner.key);
      } else {
        setError('De AI gaf geen tekst terug. Probeer het opnieuw of pas de tekst handmatig aan.');
      }
    } catch {
      setError('Genereren mislukt. Probeer het opnieuw.');
    } finally {
      setRegenerating(false);
    }
  };

  // Init: partners ophalen, default kiezen, dan prefill mét die partner zodat de
  // reply meteen op de aanvraag is afgestemd. Formulier blijft achter de loader.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) throw new Error('Niet ingelogd');

        // 1. Partners + default-keuze (segment-match op onderwerp, anders de eerste).
        const pRes = await fetch('/api/inbox/doorverwijs-partners', { headers: { Authorization: `Bearer ${token}` } });
        const body = pRes.ok
          ? ((await pRes.json().catch(() => ({}))) as {
              partners?: DoorverwijsPartner[];
              onvolledig?: OnvolledigePartner[];
              gebied?: Gebied | null;
            })
          : {};
        const list: DoorverwijsPartner[] = body.partners || [];
        if (cancelled) return;
        setPartners(list);
        setOnvolledig(body.onvolledig || []);
        setGebied(body.gebied ?? null);
        if (!pRes.ok) setLoadError('Kon doorverwijs-partners niet laden');

        // 1b. Staat er al een goedgekeurde AI-aanvraag? Dan is dát de partner en
        //     de klant; de dialoog verstuurt die aanvraag, geen tweede lead.
        const preflight = await fetchPreflight({});
        if (cancelled) return;
        const al = preflight.ok ? preflight.bestaand : null;
        if (al) {
          setBestaand(al);
          partnerHandmatig.current = true;
          if (al.klantEmail && !isEigenEmail(al.klantEmail)) setEmail(al.klantEmail);
        }

        // Standaardpartner: alleen een segmentmatch op het onderwerp, of de
        // bestaande aanvraag. Nooit "de eerste van de lijst": dat was alfabetisch
        // CleanProfit (zonnepanelen) voor een meubelaanvraag (Huygen 03-11). Zonder
        // match blijft de keuze leeg tot de postcode de uitvoerder aanwijst.
        const haystack = `${conversation?.subject ?? ''} ${conversation?.snippet ?? ''}`.toLowerCase();
        const def = (al && list.find((pp) => pp.partnerId === al.partnerId && pp.segment === al.segment))
          || list.find((pp) => pp.segment && haystack.includes(pp.segment.toLowerCase()))
          || null;
        setSelectedKey(def?.key ?? '');
        setCc(def?.ccEmail ?? '');

        // 2. Prefill (klant + context + reply afgestemd op aanvraag + default partner).
        let resolvedFirst = prefill.firstName;
        let aiReply = '';
        if (def) setReplyBezig(true);
        const data = await fetchPrefill(def);
        if (!cancelled && data) {
          const p = data.prefill || {};
          if (p.firstName) { setFirstName(p.firstName); resolvedFirst = p.firstName; }
          if (p.lastName) setLastName(p.lastName);
          // Bezorgbaar-check: de AI vist uit de bodytekst en gaf daar eerder
          // een cid:-afbeeldingsreferentie als "adres" terug; een geldig
          // header-adres mag daar nooit door overschreven worden. Eigen adres
          // of nummer (onze geciteerde handtekening) evenmin.
          if (p.email && isBezorgbaarEmail(p.email) && !isEigenEmail(p.email) && !al?.klantEmail) setEmail(p.email);
          if (p.phone && !isEigenTelefoon(p.phone)) setPhone(p.phone);
          if (p.postcode) setPostcode(p.postcode);
          if (p.context) setNote(p.context);
          if (p.firstName || p.email || p.phone || p.context) setAiPrefilled(true);
          aiReply = String(data.reply || '').trim();
        }

        // 3. Reply: AI-tekst (afgestemd op aanvraag) of de partner-template als fallback.
        if (!cancelled && def) {
          setReplyText(aiReply || fillTemplate(def.replyTemplate, resolvedFirst));
          setReplyVoorKey(def.key);
        }
        if (!cancelled) setReplyBezig(false);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : 'Laden mislukt');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // De postcode komt pas binnen met de AI-prefill (of de rep past hem aan).
  // Zodra hij bekend is opnieuw ophalen: de server hangt er dan de gebiedsrol
  // aan en zet de uitvoerder van dát gebied bovenaan. Puur advies — de rep kan
  // bewust afwijken, dus een eigen keuze blijft staan.
  useEffect(() => {
    if (loading) return;
    if (!/^\s*\d{4}/.test(postcode)) return;
    let afgebroken = false;
    const t = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const res = await fetch(
          `/api/inbox/doorverwijs-partners?postcode=${encodeURIComponent(postcode.trim())}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok || afgebroken) return;
        const body = (await res.json().catch(() => ({}))) as {
          partners?: DoorverwijsPartner[];
          onvolledig?: OnvolledigePartner[];
          gebied?: Gebied | null;
        };
        if (afgebroken) return;
        const list = body.partners || [];
        setPartners(list);
        setOnvolledig(body.onvolledig || []);
        setGebied(body.gebied ?? null);

        // Alleen bijsturen als de rep nog niets koos én er echt een uitvoerder
        // voor dit gebied is. Anders blijft de bestaande selectie staan.
        if (partnerHandmatig.current) return;
        const uitvoerder = list.find((p) => p.gebiedsrol === 'uitvoerder');
        if (uitvoerder && uitvoerder.key !== selectedKey) {
          setSelectedKey(uitvoerder.key);
          void applyPartner(uitvoerder.key, list);
        }
      } catch {
        // Gebiedshint is optioneel; stilte is hier het juiste gedrag.
      }
    }, 400);
    return () => {
      afgebroken = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postcode, loading]);

  // Handmatige partnerwissel: CC bijwerken en — tenzij de rep de tekst al
  // aanpaste — een nieuwe, op de aanvraag afgestemde reply ophalen.
  const applyPartner = async (key: string, lijst?: DoorverwijsPartner[]) => {
    const partner = (lijst ?? partners).find((p) => p.key === key) || null;
    if (!partner) return;
    setCc(partner.ccEmail);
    if (replyTouched) {
      // De medewerker schreef zelf; de tekst is van hem. De server toetst bij
      // versturen alsnog of er geen andere partnernaam in staat.
      setReplyVoorKey(key);
      return;
    }
    setReplyBezig(true);
    try {
      const data = await fetchPrefill(partner);
      const aiReply = data ? String(data.reply || '').trim() : '';
      setReplyText(aiReply || fillTemplate(partner.replyTemplate, firstName));
      setReplyVoorKey(key);
    } finally {
      setReplyBezig(false);
    }
  };

  const handleSubmit = async () => {
    if (!conversation || !selectedPartner) return;
    if (!email.trim()) {
      setError('Een e-mailadres van de klant is nodig om te antwoorden.');
      return;
    }
    if (!isBezorgbaarEmail(email)) {
      setError(`"${email.trim()}" is geen bezorgbaar e-mailadres — controleer het Aan-veld.`);
      return;
    }
    if (isEigenEmail(email)) {
      setError(`${email.trim()} is onze eigen mailbox, niet de klant. Vul het e-mailadres van de klant in.`);
      return;
    }
    if (isEigenTelefoon(phone)) {
      setError(`${phone.trim()} is ons eigen kantoornummer. Laat het veld leeg of vul het nummer van de klant in.`);
      return;
    }
    if (!replyText.trim()) {
      setError('De antwoordtekst is leeg.');
      return;
    }
    if (replyBezig) {
      setError('De antwoordtekst wordt nog bijgewerkt voor de gekozen partner. Wacht even.');
      return;
    }
    if (replyVoorKey !== selectedPartner.key) {
      setError(`De antwoordtekst is geschreven voor een andere partner dan ${selectedPartner.naam}. Genereer de tekst opnieuw of pas hem aan.`);
      return;
    }
    setError(null);
    setWarning(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Niet ingelogd');

      // ── Stap 1: reply naar de klant met partner in CC (overslaan bij retry) ──
      if (!replySent) {
        const bron = kiesActueleKlantbronBasis(activeThread?.messages ?? [], conversation);
        if (bron.status !== 'gevonden') {
          setError('Het actuele klantbericht kon niet worden vastgesteld. Herlaad het gesprek.');
          return;
        }
        setPhase('sending');
        // Preflight met dezelfde regels als de registratie: eigen adres, tekst
        // hoort bij de partner, geen tweede aanvraag naast een bestaande.
        const check = await fetchPreflight({
          partnerId: selectedPartner.partnerId,
          segment: selectedPartner.segment,
          customer: { email: email.trim(), phone: phone.trim() },
          replyText,
        });
        if (!check.ok) {
          setError(check.error);
          if (check.bestaand) setBestaand(check.bestaand);
          setPhase('idle');
          return;
        }
        const replyRes = await fetch('/api/inbox/reply', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            conversation_id: conversation.id,
            source_message_id: bron.bron.bericht.id,
            handmatig_antwoord: true,
            to: email.trim(),
            cc: cc.trim() || selectedPartner.ccEmail,
            body_html: replyText.replace(/\n/g, '<br/>'),
            body_text: replyText,
            // Telefoon/terugbel-threads hebben `mailbox` = merk-string i.p.v. een
            // verzendadres; from_brand laat reply.ts de echte mailbox resolven.
            from_brand: conversation.brand,
          }),
        });
        if (!replyRes.ok) {
          const e = await replyRes.json().catch(() => ({ error: 'Versturen mislukt' }));
          setError(e.error || 'Versturen mislukt');
          setPhase('idle');
          return;
        }
        setReplySent(true);
      }

      // ── Stap 2: doorverwijzing registreren (idempotent — veilig te herhalen) ──
      setPhase('registering');
      const regRes = await fetch('/api/inbox/doorverwijzen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          conversation_id: conversation.id,
          partnerId: selectedPartner.partnerId,
          segment: selectedPartner.segment,
          customer: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            phone: phone.trim(),
            postcode: postcode.trim() || undefined,
          },
          note: note.trim() || undefined,
          // Alleen bij %-partners + geldig bedrag; anders laat de server 't op basisprijs.
          geschatBedrag: isPercentagePartner && Number.isFinite(bedragNum) && bedragNum > 0 ? bedragNum : undefined,
          replyText,
        }),
      });
      if (!regRes.ok) {
        const e = await regRes.json().catch(() => ({ error: 'Registratie mislukt' }));
        setWarning(
          `De e-mail is verstuurd, maar de doorverwijzing registreren mislukte: ${e.error || 'onbekende fout'}. ` +
          'Klik op "Opnieuw registreren" om alleen de registratie te herhalen — de e-mail wordt niet nogmaals verstuurd.',
        );
        setPhase('idle');
        return;
      }

      const data: { partnerNaam?: string; commissieLabel?: string; bestaand?: boolean; leadNummer?: string } = await regRes.json();
      // Thread verversen zodat de reply + status 'afgehandeld' zichtbaar worden.
      await useInboxStore.getState().openThread(conversation.id);
      onSuccess?.(data.bestaand
        ? `Bestaande aanvraag ${data.leadNummer ?? ''} verstuurd naar ${data.partnerNaam ?? selectedPartner.naam}`
        : `Geregistreerd voor ${data.partnerNaam ?? selectedPartner.naam} · ${data.commissieLabel ?? selectedPartner.commissieLabel}`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Er ging iets mis');
      setPhase('idle');
    }
  };

  const submitLabel = replySent
    ? (phase === 'registering' ? 'Registreren…' : 'Opnieuw registreren')
    : (phase === 'sending' ? 'Versturen…' : phase === 'registering' ? 'Registreren…' : 'Versturen & registreren');

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={() => !isBusy && onClose()}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Doorsturen naar partner</h3>
          <button
            onClick={onClose}
            disabled={isBusy}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            title="Sluiten (Esc)"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading ? (
            <div className="py-8 flex items-center justify-center text-slate-400">
              <svg className="w-5 h-5 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Klantgegevens uit de mail halen…
            </div>
          ) : loadError ? (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">
              {loadError}
            </div>
          ) : partners.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
              <p>Geen doorverwijs-partners geconfigureerd.</p>
              {onvolledig.length > 0 && (
                <ul className="mt-1.5 space-y-0.5 text-xs">
                  {onvolledig.map((p) => (
                    <li key={p.partnerId}>
                      <span className="font-medium">{p.naam}</span>: {p.redenen.join(' + ')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <>
              {bestaand && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  Op dit gesprek staat al een goedgekeurde aanvraag <span className="font-medium">{bestaand.leadNummer}</span> voor{' '}
                  <span className="font-medium">{bestaand.partnerNaam}</span>
                  {bestaand.status === 'voorbereid' ? ' (nog niet aan de partner verstuurd)' : ` (${bestaand.status})`}.
                  Deze dialoog verstuurt díe aanvraag; er komt geen tweede lead. Een andere partner kiezen kan pas als die aanvraag is gesloten.
                </div>
              )}
              {aiPrefilled && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 text-xs text-indigo-700 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  Klantgegevens uit de mail gehaald — controleer en pas zo nodig aan.
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Partner</label>
                <select
                  value={selectedKey}
                  onChange={(e) => {
                    partnerHandmatig.current = true;
                    setSelectedKey(e.target.value);
                    void applyPartner(e.target.value);
                  }}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {!selectedKey && <option value="">Kies een partner…</option>}
                  {partners.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.naam} — {p.segment} ({p.commissieLabel})
                      {p.gebiedsrol === 'uitvoerder' ? ' · uitvoerder hier' : ''}
                      {p.gebiedsrol === 'andere-partner' ? ' · ander gebied' : ''}
                    </option>
                  ))}
                  {onvolledig.length > 0 && (
                    <optgroup label="Nog niet doorverwijsbaar">
                      {onvolledig.map((p) => (
                        <option key={p.partnerId} value={p.partnerId} disabled>
                          {p.naam} — {p.redenen.join(' + ')}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {gebied && <GebiedsHint gebied={gebied} gekozen={selectedPartner} />}
                {onvolledig.length > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {onvolledig.length === 1 ? '1 partner staat' : `${onvolledig.length} partners staan`} grijs:
                    los het in Salesforce op, dan {onvolledig.length === 1 ? 'verschijnt hij' : 'verschijnen ze'} hier
                    vanzelf.
                  </p>
                )}
              </div>

              {isPercentagePartner && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Geschat opdrachtbedrag <span className="text-slate-400">(optioneel, excl. btw)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={geschatBedrag}
                      onChange={(e) => setGeschatBedrag(e.target.value)}
                      placeholder="bv. 350"
                      className="w-full pl-7 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {commissiePreview != null
                      ? `Voorlopige commissie ≈ €${commissiePreview.toFixed(2).replace('.', ',')} (${selectedPartner!.commissieLabel}). Wordt later definitief.`
                      : `Voor de voorlopige ${selectedPartner!.commissieLabel}. Leeg laten mag — dan schatten we op basisprijs.`}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Voornaam</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Achternaam</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">E-mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Telefoon</label>
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Postcode <span className="text-slate-400">(optioneel)</span>
                  </label>
                  <input
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  CC (partner) <span className="text-slate-400">— ontvangt deze e-mail mee</span>
                </label>
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Extra context voor de AI <span className="text-slate-400">(optioneel — stuurt de mail, komt er niet letterlijk in)</span>
                </label>
                <textarea
                  value={extraContext}
                  onChange={(e) => setExtraContext(e.target.value)}
                  rows={2}
                  placeholder="bv. Alleen de bank doorsturen — het vloerkleed loopt al via Kleda"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-slate-500">Antwoord aan klant</label>
                  <button
                    type="button"
                    onClick={() => void regenerateReply()}
                    disabled={regenerating || isBusy || !selectedPartner}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                    title="Genereer de mail opnieuw met de extra context"
                  >
                    {regenerating ? (
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                      </svg>
                    )}
                    {regenerating ? 'Genereren…' : 'Genereer opnieuw met AI'}
                  </button>
                </div>
                <textarea
                  value={replyText}
                  onChange={(e) => { setReplyText(e.target.value); setReplyTouched(true); }}
                  rows={8}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[160px]"
                />
                {replyBezig ? (
                  <p className="text-xs text-amber-700 mt-1">Antwoordtekst wordt bijgewerkt voor {selectedPartner?.naam ?? 'de gekozen partner'}…</p>
                ) : selectedPartner && replyVoorKey && replyVoorKey !== selectedPartner.key ? (
                  <p className="text-xs text-amber-700 mt-1">Deze tekst is geschreven voor een andere partner dan {selectedPartner.naam}. Genereer opnieuw of pas hem aan.</p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">Handtekening wordt automatisch toegevoegd.</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Interne notitie <span className="text-slate-400">(optioneel — komt op de lead, niet in de mail)</span>
                </label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="bv. Klant belde, wil bankstel reinigen in Utrecht"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">
                  {error}
                </div>
              )}
              {warning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
                  {warning}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isBusy}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            onClick={handleSubmit}
            disabled={isBusy || loading || replyBezig || !selectedPartner || !email.trim() || replyVoorKey !== selectedPartner.key}
            className="px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isBusy && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Eén regel die vertelt in welk postcodegebied de klant zit en of de gekozen
 * partner daar de uitvoerder is. Bewust adviserend: afwijken mag, maar dan
 * bewust. De gebiedstoewijzing zelf staat op de DPA en wordt beheerd op
 * /gebieden; hier tonen we alleen wat daar is ingesteld.
 */
function GebiedsHint({
  gebied,
  gekozen,
}: {
  gebied: Gebied;
  gekozen: DoorverwijsPartner | null;
}) {
  const gebiedNaam = gebied.gebiedNaam ?? `postcode ${gebied.postcode}`;

  if (!gekozen) {
    return <p className="mt-1 text-xs text-slate-500">Klant zit in {gebiedNaam}.</p>;
  }

  if (gekozen.gebiedsrol === 'uitvoerder') {
    return (
      <p className="mt-1 text-xs text-emerald-700">
        {gekozen.naam} is de uitvoerder voor {gekozen.segment} in {gebiedNaam}.
        {gebied.meerdereGebieden && ' Let op: meerdere postcodegebieden dekken deze postcode.'}
      </p>
    );
  }

  if (gekozen.gebiedsrol === 'andere-partner') {
    const echte = gebied.uitvoerders[gekozen.segment]?.partnerNaam;
    return (
      <p className="mt-1 text-xs text-amber-700">
        In {gebiedNaam} is {echte ?? 'een andere partner'} de uitvoerder voor {gekozen.segment}. Doorsturen
        naar {gekozen.naam} kan, maar wijkt af van de gebiedstoewijzing.
      </p>
    );
  }

  if (gekozen.gebiedsrol === 'niet-toegewezen') {
    return (
      <p className="mt-1 text-xs text-amber-700">
        {gekozen.segment} is niet opengesteld in {gebiedNaam} — er staat geen uitvoerder voor dit gebied.
        Doorsturen kan, maar leg de toewijzing daarna vast op /gebieden.
      </p>
    );
  }

  return <p className="mt-1 text-xs text-slate-500">Klant zit in {gebiedNaam}.</p>;
}
